import React from "react";
import type {
  SspPartyResponse,
  SspRoleResponse,
} from "../../../../../api/frameworksApi";
import {
  FormField,
  FormInput,
  FormSelect,
  Modal,
  ModalFooterButtons,
} from "../../../../components/Modal";

export interface AssignResponsiblePartyDialogProps {
  open: boolean;
  onClose: () => void;
  handleAssignRP: () => void;
  assigningRP: boolean;
  rpForm: { roleId: string; partyUuids: string };
  setRpForm: React.Dispatch<
    React.SetStateAction<{ roleId: string; partyUuids: string }>
  >;
  roles: SspRoleResponse[];
  parties: SspPartyResponse[];
}

export function AssignResponsiblePartyDialog({
  open,
  onClose,
  handleAssignRP,
  assigningRP,
  rpForm,
  setRpForm,
  roles,
  parties,
}: AssignResponsiblePartyDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Assign Responsible Party"
      description="Map a role to one or more parties"
      size="sm"
      footer={
        <ModalFooterButtons
          onCancel={onClose}
          onSubmit={handleAssignRP}
          submitLabel={assigningRP ? "Assigning" : "Assign"}
          submitDisabled={!rpForm.roleId || !rpForm.partyUuids || assigningRP}
        />
      }
    >
      <FormField label="Role" required>
        <FormSelect
          options={roles.map((r) => ({
            value: r.roleId,
            label: `${r.title} (${r.roleId})`,
          }))}
          placeholder="Select role"
          value={rpForm.roleId}
          onChange={(v) => setRpForm((f) => ({ ...f, roleId: v }))}
        />
      </FormField>
      <FormField label="Parties" required>
        {parties.length > 0 ? (
          <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] bg-[#f5f7fb] dark:bg-white/[0.04] max-h-40 overflow-y-auto">
            {parties.map((p) => {
              const selected = rpForm.partyUuids
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);
              const checked = selected.includes(p.partyUuid);
              return (
                <label
                  key={p.id}
                  className="flex items-center gap-2.5 px-3 py-2 hover:bg-black/[0.03] dark:hover:bg-white/[0.03] cursor-pointer transition-colors border-b border-[#e2e8f0]/50 dark:border-white/[0.04] last:border-0"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      setRpForm((f) => {
                        const current = f.partyUuids
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean);
                        const next = checked
                          ? current.filter((u) => u !== p.partyUuid)
                          : [...current, p.partyUuid];
                        return { ...f, partyUuids: next.join(", ") };
                      })
                    }
                    className="size-3.5 rounded border-border accent-indigo-600 cursor-pointer"
                  />
                  <span className="text-sm text-foreground">
                    {p.name}{" "}
                    <span className="text-xs text-muted-foreground">
                      ({p.partyType})
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        ) : (
          <FormInput
            placeholder="Paste party UUIDs (comma-separated)"
            value={rpForm.partyUuids}
            onChange={(v) => setRpForm((f) => ({ ...f, partyUuids: v }))}
          />
        )}
      </FormField>
    </Modal>
  );
}
